export const CAMERA_ERROR_CODE = {
  PERMISSION_DENIED: 'permission-denied',
  NO_CAMERA: 'no-camera',
  CAMERA_IN_USE: 'camera-in-use',
  CONSTRAINTS_NOT_SATISFIABLE: 'constraints-not-satisfiable',
  ABORTED: 'aborted',
  UNKNOWN: 'unknown',
};

// error.name -> our code. Covers every DOMException getUserMedia/enumerateDevices
// can actually throw, plus the legacy Firefox names still seen in the wild.
const NAME_TO_CODE = {
  NotAllowedError: CAMERA_ERROR_CODE.PERMISSION_DENIED,
  PermissionDeniedError: CAMERA_ERROR_CODE.PERMISSION_DENIED,
  SecurityError: CAMERA_ERROR_CODE.PERMISSION_DENIED,
  NotFoundError: CAMERA_ERROR_CODE.NO_CAMERA,
  DevicesNotFoundError: CAMERA_ERROR_CODE.NO_CAMERA,
  NotReadableError: CAMERA_ERROR_CODE.CAMERA_IN_USE,
  TrackStartError: CAMERA_ERROR_CODE.CAMERA_IN_USE,
  OverconstrainedError: CAMERA_ERROR_CODE.CONSTRAINTS_NOT_SATISFIABLE,
  ConstraintNotSatisfiedError: CAMERA_ERROR_CODE.CONSTRAINTS_NOT_SATISFIABLE,
  // No handling existed anywhere in the app for this before — iOS/Safari can
  // raise it when a capture request is torn down while still pending (e.g. a
  // fast recovery remount racing a prior in-flight getUserMedia).
  AbortError: CAMERA_ERROR_CODE.ABORTED,
};

const DEFAULT_MESSAGE = {
  [CAMERA_ERROR_CODE.PERMISSION_DENIED]: 'Camera access was denied.',
  [CAMERA_ERROR_CODE.NO_CAMERA]: 'No camera was found on this device.',
  [CAMERA_ERROR_CODE.CAMERA_IN_USE]: 'The camera is being used by another application.',
  [CAMERA_ERROR_CODE.CONSTRAINTS_NOT_SATISFIABLE]:
    'The camera could not satisfy the requested settings.',
  [CAMERA_ERROR_CODE.ABORTED]: 'The camera request was interrupted.',
  [CAMERA_ERROR_CODE.UNKNOWN]: 'The camera could not be started.',
};

/**
 * Turns a raw getUserMedia/enumerateDevices error into a stable, typed shape
 * callers can actually branch on — denial vs. no-hardware vs. in-use vs.
 * aborted vs. unsatisfiable are different facts with different fixes, and
 * collapsing them all into one boolean (the old requestMediaPermissions())
 * threw that distinction away.
 */
export function classifyCameraError(error) {
  const name = error?.name ?? 'Unknown';
  const code = NAME_TO_CODE[name] ?? CAMERA_ERROR_CODE.UNKNOWN;

  return {
    code,
    name,
    message: error?.message ?? '',
    userMessage: DEFAULT_MESSAGE[code],
    raw: error,
  };
}
